import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Loader2, Download, ChevronLeft, BookOpen, Award, FileText } from "lucide-react";

export default function PDCourse() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setErr("missing"); return; }
    api.get(`/pd/access?token=${token}`)
      .then(r => setData(r.data))
      .catch(() => setErr("invalid"));
  }, [token]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="w-14 h-14 mx-auto bg-rose-100 text-rose-600 rounded-2xl flex items-center justify-center mb-4">
          <FileText strokeWidth={2.5}/>
        </div>
        <h1 className="text-3xl font-black text-slate-900">Invalid course link</h1>
        <p className="text-slate-600 mt-2">Looks like this link expired or isn't valid. Please re-enroll or check your email for the original link.</p>
        <Link to="/professional-development"><Button className="mt-6 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-bold h-11 px-6">Go to PD page</Button></Link>
      </div>
    );
  }

  if (!data) return <div className="p-12 text-center text-slate-500"><Loader2 className="w-6 h-6 mx-auto animate-spin"/> Loading your course…</div>;

  return (
    <div data-testid="pd-course-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/professional-development" className="inline-flex items-center text-sm font-bold text-slate-500 hover:text-slate-900 mb-6">
        <ChevronLeft size={16} strokeWidth={3}/> Back
      </Link>

      <div className="bg-gradient-to-br from-violet-600 to-amber-500 rounded-3xl p-8 lg:p-10 text-white mb-8 relative overflow-hidden">
        <div className="absolute -bottom-12 -right-12 w-64 h-64 bg-white/10 rounded-full blur-3xl"/>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 mb-4">
            <Award size={14} strokeWidth={3}/>
            <span className="text-[10px] font-black uppercase tracking-widest">Welcome, {data.teacher_name || "educator"}</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight mb-3">{data.title}</h1>
          <p className="text-white/90">Your private course dashboard. Bookmark this page or use the link in your email.</p>
        </div>
      </div>

      <section className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-700">
            <BookOpen size={14} strokeWidth={3}/> Course material
          </div>
          <div className="flex items-center gap-2">
            <a href={data.pdf_url} target="_blank" rel="noreferrer" data-testid="pd-open-tab"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm">
              Open in new tab
            </a>
            <a href={data.pdf_url} download data-testid="pd-download"
               onClick={() => { api.post("/pd/track-download", { token }).catch(() => {}); }}
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-bold text-sm">
              <Download size={14} strokeWidth={2.5}/> Download
            </a>
          </div>
        </div>
        <div className="bg-slate-50" style={{ height: "min(85vh, 1100px)" }}>
          <iframe src={data.pdf_url} title={data.title} data-testid="pd-content"
                  className="w-full h-full border-0 bg-white" />
        </div>
      </section>

      <div className="mt-6 text-center text-xs text-slate-500">
        Need help? Email <a href="mailto:newagelearning@thehaddisacademy.com" className="font-bold text-sky-700 underline">newagelearning@thehaddisacademy.com</a>
      </div>
    </div>
  );
}
